/**
 * Draft ↔ Spec drift comparison (read-only).
 *
 * Pure helper used by the Ordering Queue to surface BOM drift between a
 * prepared OrderDraft and the currently-bound JobSpec BOM, BEFORE the draft
 * is pushed to a vendor.
 *
 * Posture (matches Phase 4A.5): non-blocking. We only surface drift; we
 * never mutate the draft, the spec, or the push pipeline. This complements
 * — does not replace — the Material Alignment Gate that runs against the
 * pushed PO downstream.
 *
 * Three drift cases:
 *   • Quantity mismatch  — SKU is on both sides, qty differs.
 *   • Missing from order — SKU is on the spec, not on the draft.
 *   • Not on spec        — SKU is on the draft, not on the spec.
 *
 * "Spec changed since prep" is detected separately and surfaced with neutral
 * copy ("Spec changed since draft was prepared") rather than as operator
 * error — the operator may not have introduced the drift.
 */

import type { Job } from "@/data/types";
import type { OrderDraft } from "@/data/orderDraft";
import type { JobSpecBomItem } from "@/data/jobSpec";
import { getJobBomRequirements } from "@/data/inventory/allocationValidation";

export type DraftDriftKind = "qty_mismatch" | "missing_from_order" | "not_on_spec";

export interface DraftDriftRow {
  sku: string;
  productName: string;
  kind: DraftDriftKind;
  /** Quantity on the bound spec BOM (undefined for not_on_spec). */
  specQty?: number;
  /** Quantity summed across all draft vendor groups (undefined for missing_from_order). */
  draftQty?: number;
}

export interface DraftSpecDriftReport {
  /** True when no bound JobSpec exists — caller should render a neutral empty state. */
  noSpec: boolean;
  /** True when the draft's pinned specRef differs from the currently bound spec. */
  specChangedSincePrep: boolean;
  rows: DraftDriftRow[];
  counts: {
    qtyMismatch: number;
    missingFromOrder: number;
    notOnSpec: number;
    total: number;
  };
}

/** Aggregate draft line items by SKU across all vendor groups. */
function sumDraftBySku(draft: OrderDraft): Map<string, { quantity: number; productName: string }> {
  const out = new Map<string, { quantity: number; productName: string }>();
  for (const vg of draft.vendorGroups) {
    for (const li of vg.items) {
      const prev = out.get(li.sku);
      out.set(li.sku, {
        quantity: (prev?.quantity ?? 0) + li.quantity,
        productName: prev?.productName ?? li.productName,
      });
    }
  }
  return out;
}

/**
 * Compare a prepared draft against the bound spec BOM. Pure / deterministic.
 * Returns an empty report (rows: []) when no bound spec exists.
 */
export function compareDraftToSpec(
  job: Pick<Job, "boundJobSpec">,
  draft: OrderDraft | undefined,
): DraftSpecDriftReport {
  const empty: DraftSpecDriftReport = {
    noSpec: !job.boundJobSpec,
    specChangedSincePrep: false,
    rows: [],
    counts: { qtyMismatch: 0, missingFromOrder: 0, notOnSpec: 0, total: 0 },
  };

  if (!draft) return empty;

  const reqs: Map<string, JobSpecBomItem> = getJobBomRequirements(job);
  if (reqs.size === 0) return { ...empty, noSpec: true };

  const draftBySku = sumDraftBySku(draft);
  const rows: DraftDriftRow[] = [];

  // Spec-side iteration — qty mismatch + missing_from_order.
  for (const [sku, item] of reqs) {
    const draftLine = draftBySku.get(sku);
    if (!draftLine) {
      rows.push({
        sku,
        productName: item.name,
        kind: "missing_from_order",
        specQty: item.quantity,
      });
      continue;
    }
    if (draftLine.quantity !== item.quantity) {
      rows.push({
        sku,
        productName: item.name,
        kind: "qty_mismatch",
        specQty: item.quantity,
        draftQty: draftLine.quantity,
      });
    }
  }

  // Draft-side iteration — not_on_spec.
  for (const [sku, line] of draftBySku) {
    if (!reqs.has(sku)) {
      rows.push({
        sku,
        productName: line.productName,
        kind: "not_on_spec",
        draftQty: line.quantity,
      });
    }
  }

  // Stable sort: severity (missing → qty → extra), then SKU.
  const order: Record<DraftDriftKind, number> = {
    missing_from_order: 0,
    qty_mismatch: 1,
    not_on_spec: 2,
  };
  rows.sort((a, b) => order[a.kind] - order[b.kind] || a.sku.localeCompare(b.sku));

  // "Spec changed since prep" — pinned ref vs current bound ref.
  const currentRef = job.boundJobSpec
    ? `${job.boundJobSpec.specId}-v${job.boundJobSpec.versionNumber}`
    : "legacy:materialsList";
  const specChangedSincePrep = !!draft.sourceSpecRef && draft.sourceSpecRef !== currentRef;

  const counts = {
    qtyMismatch: rows.filter((r) => r.kind === "qty_mismatch").length,
    missingFromOrder: rows.filter((r) => r.kind === "missing_from_order").length,
    notOnSpec: rows.filter((r) => r.kind === "not_on_spec").length,
    total: rows.length,
  };

  return { noSpec: false, specChangedSincePrep, rows, counts };
}

/**
 * Short, human-readable label for a drift kind. Neutral copy.
 */
export function labelForDriftKind(kind: DraftDriftKind): string {
  switch (kind) {
    case "missing_from_order":
      return "Missing from order";
    case "qty_mismatch":
      return "Qty mismatch";
    case "not_on_spec":
      return "Not on spec";
  }
}
